import { InspectionRecord } from '../services/localBackend';
import { getDefectTypeLabel, getSeverityLabel, getSeverityBadgeClass } from '../hooks/useQueries';
import { Skeleton } from '@/components/ui/skeleton';

interface RecentInspectionsTableProps {
  records: InspectionRecord[];
  loading?: boolean;
  maxRows?: number;
}

function formatTimestamp(timestamp: number): string {
  return new Date(timestamp).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function RecentInspectionsTable({
  records,
  loading = false,
  maxRows = 10,
}: RecentInspectionsTableProps) {
  const displayRecords = records.slice(0, maxRows);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Component ID
            </th>
            <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Timestamp
            </th>
            <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Defect Type
            </th>
            <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Severity
            </th>
            <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Classification
            </th>
            <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Confidence
            </th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} className="border-b border-border/50">
                {Array.from({ length: 6 }).map((_, j) => (
                  <td key={j} className="py-3 px-3">
                    <Skeleton className="h-4 w-full bg-muted/50" />
                  </td>
                ))}
              </tr>
            ))
          ) : displayRecords.length === 0 ? (
            <tr>
              <td colSpan={6} className="py-8 text-center text-muted-foreground text-sm">
                No inspection records found
              </td>
            </tr>
          ) : (
            displayRecords.map((record, idx) => (
              <tr
                key={idx}
                className="border-b border-border/50 hover:bg-muted/20 transition-colors"
              >
                <td className="py-3 px-3 font-mono text-xs text-electric-blue">
                  {record.componentId}
                </td>
                <td className="py-3 px-3 text-xs text-muted-foreground">
                  {formatTimestamp(record.timestamp)}
                </td>
                <td className="py-3 px-3 text-xs text-foreground">
                  {getDefectTypeLabel(record.defectType)}
                </td>
                <td className="py-3 px-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden max-w-16">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${record.severityScore}%`,
                          background: Number(record.severityScore) >= 65
                            ? 'oklch(0.58 0.22 25)'
                            : Number(record.severityScore) >= 30
                              ? 'oklch(0.75 0.18 75)'
                              : 'oklch(0.65 0.18 145)',
                        }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground font-mono">{String(record.severityScore)}</span>
                  </div>
                </td>
                <td className="py-3 px-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${getSeverityBadgeClass(record.finalClassification)}`}>
                    {getSeverityLabel(record.finalClassification)}
                  </span>
                </td>
                <td className="py-3 px-3 text-right font-mono text-xs text-muted-foreground">
                  {String(record.modelConfidence)}%
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
